import React from "react";
import { Text, Flex, Box, type TextProps } from "@chakra-ui/react";
import Image from "next/image";
import { type Customer } from "lib/sanity";
import { Balancer } from "react-wrap-balancer";
import { Section, type SectionProps } from "components/section";
import { getCustomerLogoUrl, getCustomerLogoStyles } from "utils";
import { type Theme } from "contexts/color-theme";
import Link from "next/link";
import { trackCustomerLogosClicked } from "components/event-tracking";

type CustomerProps = {
  customers: Customer[];
  title?: string;
  titleMaxW?: TextProps["maxW"];
  theme?: Theme;
} & SectionProps;

/**
 * A block to display center-aligned logos in a flex container, used in cases where we don't
 * have exactly 8 or 16 logos to display in `StackedLogos`.
 *
 * Since the logos will automatically wrap to a new line, this generally looks best for either:
 *
 * - 6 or fewer logos
 * - 9 or more logos
 *
 * With that changing slightly depending on the aspect ratio of all the logos provided.
 */
export function Customers({
  customers,
  titleMaxW,
  title = "Trusted by teams at",
  theme = "light",
  ...props
}: CustomerProps) {
  return (
    <Section {...props}>
      <Flex
        align="center"
        direction="column"
        gap={{ base: "bigger", md: "biggest" }}
        justify="start"
        maxW={{ base: "520px", md: "740px", lg: "1000px" }}
        mx="auto"
        w="100%"
      >
        {title && (
          <Text textStyle={{ base: "headline3", md: "headline2" }} textAlign="center" maxW={titleMaxW} mx="auto">
            <Balancer>{title}</Balancer>
          </Text>
        )}

        <Flex
          as={Link}
          aria-label="Read case studies"
          href="/customers"
          borderRadius="base"
          flex={1}
          gap={{ base: "small", md: "big", xl: "bigger" }}
          flexWrap="wrap"
          align="center"
          justify="center"
          overflow="hidden"
          transition="all 0.15s ease-in-out"
          _focus={{ outline: "none !important" }}
          _focusVisible={{
            outline: "none !important",
            boxShadow: "outline",
          }}
          onClick={() => {
            trackCustomerLogosClicked("customers");
          }}
        >
          {customers.filter(Boolean).map((customer: Customer) => (
            <Box
              key={customer.slug}
              pos="relative"
              flex={{ base: "none", md: 1 }}
              w={{ base: "70px", md: "100px", lg: "140px" }}
              minW={{ base: "60px", md: "80px", lg: "96px" }}
              maxW={{ base: "100px", md: "120px", lg: "140px" }}
              mx={2}
              {...getCustomerLogoStyles(theme)}
            >
              <Image
                src={`${getCustomerLogoUrl(customer, theme)}?w=300`}
                alt={`${customer.name}.`}
                sizes="100%"
                width={5}
                height={2}
                style={{
                  objectFit: "contain",
                  width: "100%",
                  height: "unset",
                  maxWidth: "120px",
                  maxHeight: "40px",
                }}
              />
            </Box>
          ))}
        </Flex>
      </Flex>
    </Section>
  );
}
